import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { AbstractCellEditor, CustomCellEditorParams } from '@app/shared/components/cell-edit/abstract.cell-editor';
import dayjs from 'dayjs';

@Component({
  selector: 'date-time-cell-edit',
  templateUrl: './date-time-cell-edit.component.html',
  styleUrls: ['./date-time-cell-edit.component.scss'],
  standalone: false
})
export class DateTimeCellEditComponent extends AbstractCellEditor {

  /**
   * Reformat a string represented date-time value from the form field as an ISO-8601 UTC date-time. Return an empty
   * string if the value is null or not a valid date-time.
   * @param value Form value.
   * @return ISO-8601 representation of the date-time or empty string.
   * @private
   */
  private formatDate(value?: string): string {
    // Note: transform falsy values to null so resulting date is invalid when value is undefined
    const date = dayjs(value ?? null);
    return date.isValid() ? date.format() : '';
  }

  /**
   * Initialisation method for the render component
   * @param params A merge of default cell editor params and custom params
   */
  public agInit(params: CustomCellEditorParams): void {
    super.agInit(params);
    // Input must be a dayjs compatible value
    const dateValue = dayjs(this.params.value ?? null);
    this.control.setValue(dateValue.isValid() ? dateValue.format(this.appConfig.DATETIME_FORMAT) : null);
  }

  /**
   * Return the final value - called by the grid once after editing is complete
   * @return String representation of a date
   */
  public getValue(): string {
    return this.formatDate(this.control.value);
  }

  /**
   * Update tooltip message in case of validation error
   * @param type Type of the validator in error
   * @param definition Definition of the validator in error
   */
  protected setTooltip(type: ValidatorType, definition: string): void {
    this.errorTooltip = this.translateService.instant(
      `ERROR.FIELD_${type}`.toUpperCase(),
      {value: this.formatDate(definition)}
    );
  }
}
